import React, { FC } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Chip } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import { ClientDepartmentFragment, ClientLocationFragment } from 'api';

import { TextTransform } from 'components';

import { UserSearchesV2Filter } from './type';

interface Props {
  filter: UserSearchesV2Filter;
  setFilter: (filter: UserSearchesV2Filter) => void;
  isEmpty: boolean;
  selectedDepartments: Array<Partial<ClientDepartmentFragment>>;
  selectedLocations: Array<Partial<ClientLocationFragment>>;
}

const useStyles = makeStyles(theme => ({
  container: {
    paddingTop: theme.spacing(2),
    '& > div': {
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  },
}));

const Chips: FC<Props> = ({
  filter,
  setFilter,
  isEmpty,
  selectedDepartments,
  selectedLocations,
}) => {
  const classes = useStyles();

  function onDelete(type: 'department' | 'location', id: string) {
    switch (type) {
      case 'department':
        setFilter({
          ...filter,
          department_ids: filter.department_ids.filter(department => department !== id),
        });
        return;
      case 'location':
        setFilter({
          ...filter,
          location_ids: filter.location_ids.filter(location => location !== id),
        });
        return;
    }
  }

  function renderDepartments() {
    if (!filter.department_ids.length) {
      return null;
    }

    return filter.department_ids.map(id => {
      const dept = selectedDepartments.find(department => department.id === id);

      return (
        <Chip
          key={id}
          clickable
          variant="outlined"
          label={dept.title}
          onDelete={() => onDelete('department', id)}
          deleteIcon={<CloseIcon fontSize="small" />}
        />
      );
    });
  }

  function renderLocations() {
    if (!filter.location_ids.length) {
      return null;
    }

    return filter.location_ids.map(id => {
      const loc = selectedLocations.find(location => location.id === id);
      return (
        <Chip
          key={id}
          clickable
          variant="outlined"
          label={loc.title}
          onDelete={() => onDelete('location', id)}
          deleteIcon={<CloseIcon fontSize="small" />}
        />
      );
    });
  }

  function renderUserRole() {
    if (!filter.user_type) {
      return null;
    }

    return (
      <Chip
        clickable
        variant="outlined"
        label={<TextTransform transform="capitalize">{filter.user_type}</TextTransform>}
        onDelete={() => {
          delete filter.user_type;
          setFilter({ ...filter });
        }}
        deleteIcon={<CloseIcon fontSize="small" />}
      />
    );
  }

  if (isEmpty) {
    return null;
  }

  return (
    <div className={classes.container}>
      {renderDepartments()}
      {renderLocations()}
      {renderUserRole()}
    </div>
  );
};

export default Chips;
